/**
 * WorkspaceLookupError — typed error mapped from leadbilling's `AuthRole`
 * middleware response (HTTP 502, `errors.code === 'workspace_lookup_failed'`).
 *
 * Surfaced when leadbilling's call to `workspace-service` fails (service
 * down, timeout, etc.). Distinct from generic 5xx because the user can
 * usefully retry in a moment. Caught in `ConfirmPurchaseModal` to render
 * an informative toast while keeping the modal open.
 *
 * Aligns with the repo's `tClassifiedError` pattern (see
 * `NoPaymentMethodError`): an `Error` carrying a stable `code` discriminator
 * that consumers detect via `instanceof` (preferred) or duck-type on
 * `error.code`.
 */
export class WorkspaceLookupError extends Error {
  /** Discriminator matching the backend `errors.code` machine code. */
  readonly code = 'workspace_lookup_failed' as const;

  constructor(
    message = 'Workspace lookup failed (workspace-service unreachable).',
  ) {
    super(message);
    this.name = 'WorkspaceLookupError';
    // Restore prototype chain after extending built-in Error (TS target ES5+ safety).
    Object.setPrototypeOf(this, WorkspaceLookupError.prototype);
  }
}

/** Type-guard for non-instanceof contexts (e.g. structured-clone boundaries). */
export const isWorkspaceLookupError = (
  err: unknown,
): err is WorkspaceLookupError =>
  err instanceof WorkspaceLookupError ||
  (err instanceof Error &&
    'code' in err &&
    (err as { code?: unknown }).code === 'workspace_lookup_failed');
